/**
 * Demo Sync Status Data
 * 
 * Realistic discovery/sync status and health checks for demo mode.
 * Includes sync history, health metrics, and recent changes.
 */

export interface DemoSyncRecord {
  id: string;
  date: string;
  status: 'success' | 'warning' | 'error';
  duration: number; // seconds
  mailboxesProcessed: number;
  campaignsProcessed: number;
  emailsSynced: number;
  errors: number;
  details: string;
}

export interface DemoHealthCheck {
  id: string;
  name: string;
  status: 'healthy' | 'warning' | 'error';
  lastChecked: string;
  details: string;
  icon: string;
}

export interface DemoChange {
  id: string;
  type: 'new_campaign' | 'mailbox_warmup' | 'new_leads' | 'campaign_update' | 'mailbox_auth' | 'sync_improvement';
  description: string;
  timestamp: string;
  impact: 'low' | 'medium' | 'high';
  status: 'completed' | 'in_progress' | 'pending';
}

export interface DemoSyncStatus {
  lastSyncTime: string;
  syncStatus: 'success' | 'in_progress' | 'warning' | 'error';
  totalMailboxes: number;
  totalCampaigns: number;
  totalClients: number;
  emailsSyncedToday: number;
  syncHistory: DemoSyncRecord[];
  healthChecks: DemoHealthCheck[];
  recentChanges: DemoChange[];
  successRate: number;
  avgSyncTime: number;
  dataVolume: {
    totalEmails: number;
    totalLeads: number;
    totalReplies: number;
    totalBounces: number;
  };
}

export const DEMO_SYNC_STATUS: DemoSyncStatus = {
  lastSyncTime: new Date(Date.now() - 5 * 60 * 1000).toISOString(), // 5 minutes ago
  syncStatus: 'success',
  totalMailboxes: 156,
  totalCampaigns: 15,
  totalClients: 8,
  emailsSyncedToday: 2847,
  syncHistory: [
    {
      id: 'sync-001',
      date: '2024-01-15T14:30:00Z',
      status: 'success',
      duration: 67,
      mailboxesProcessed: 156,
      campaignsProcessed: 15,
      emailsSynced: 2847,
      errors: 0,
      details: 'Full sync completed successfully'
    },
    {
      id: 'sync-002',
      date: '2024-01-15T08:15:00Z',
      status: 'success',
      duration: 89,
      mailboxesProcessed: 156,
      campaignsProcessed: 15,
      emailsSynced: 2156,
      errors: 0,
      details: 'Morning sync completed successfully'
    },
    {
      id: 'sync-003',
      date: '2024-01-14T20:45:00Z',
      status: 'success',
      duration: 72,
      mailboxesProcessed: 156,
      campaignsProcessed: 15,
      emailsSynced: 1923,
      errors: 0,
      details: 'Evening sync completed successfully'
    },
    {
      id: 'sync-004',
      date: '2024-01-14T14:20:00Z',
      status: 'warning',
      duration: 134,
      mailboxesProcessed: 154,
      campaignsProcessed: 15,
      emailsSynced: 1876,
      errors: 2,
      details: 'Sync completed with 2 mailbox authentication issues'
    },
    {
      id: 'sync-005',
      date: '2024-01-14T08:30:00Z',
      status: 'success',
      duration: 58,
      mailboxesProcessed: 156,
      campaignsProcessed: 15,
      emailsSynced: 2103,
      errors: 0,
      details: 'Morning sync completed successfully'
    },
    {
      id: 'sync-006',
      date: '2024-01-13T20:15:00Z',
      status: 'success',
      duration: 76,
      mailboxesProcessed: 156,
      campaignsProcessed: 15,
      emailsSynced: 1987,
      errors: 0,
      details: 'Evening sync completed successfully'
    },
    {
      id: 'sync-007',
      date: '2024-01-13T14:45:00Z',
      status: 'success',
      duration: 63,
      mailboxesProcessed: 156,
      campaignsProcessed: 15,
      emailsSynced: 2054,
      errors: 0,
      details: 'Afternoon sync completed successfully'
    }
  ],
  healthChecks: [
    {
      id: 'health-001',
      name: 'API Connection',
      status: 'healthy',
      lastChecked: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
      details: 'Smartlead API responding normally',
      icon: 'CheckCircle'
    },
    {
      id: 'health-002',
      name: 'Mailbox Access',
      status: 'warning',
      lastChecked: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
      details: '2 mailboxes need re-authentication',
      icon: 'AlertTriangle'
    },
    {
      id: 'health-003',
      name: 'Campaign Data',
      status: 'healthy',
      lastChecked: new Date(Date.now() - 3 * 60 * 1000).toISOString(),
      details: 'All campaign data up-to-date',
      icon: 'CheckCircle'
    },
    {
      id: 'health-004',
      name: 'Email Sync',
      status: 'healthy',
      lastChecked: new Date(Date.now() - 1 * 60 * 1000).toISOString(),
      details: 'Email synchronization working normally',
      icon: 'CheckCircle'
    },
    {
      id: 'health-005',
      name: 'Database Health',
      status: 'healthy',
      lastChecked: new Date(Date.now() - 4 * 60 * 1000).toISOString(),
      details: 'Database performance optimal',
      icon: 'CheckCircle'
    },
    {
      id: 'health-006',
      name: 'Rate Limits',
      status: 'healthy',
      lastChecked: new Date(Date.now() - 6 * 60 * 1000).toISOString(),
      details: 'API rate limits within normal range',
      icon: 'CheckCircle'
    }
  ],
  recentChanges: [
    {
      id: 'change-001',
      type: 'new_campaign',
      description: '3 new campaigns added this week',
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      impact: 'medium',
      status: 'completed'
    },
    {
      id: 'change-002',
      type: 'mailbox_warmup',
      description: '8 mailboxes completed warmup process',
      timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      impact: 'high',
      status: 'completed'
    },
    {
      id: 'change-003',
      type: 'new_leads',
      description: '47 new leads imported from campaigns',
      timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      impact: 'medium',
      status: 'completed'
    },
    {
      id: 'change-004',
      type: 'campaign_update',
      description: '12 campaigns updated with new sequences',
      timestamp: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
      impact: 'low',
      status: 'completed'
    },
    {
      id: 'change-005',
      type: 'mailbox_auth',
      description: '2 mailboxes require re-authentication',
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
      impact: 'medium',
      status: 'in_progress'
    },
    {
      id: 'change-006',
      type: 'sync_improvement',
      description: 'Sync performance improved by 15%',
      timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      impact: 'high',
      status: 'completed'
    }
  ],
  successRate: 98.5,
  avgSyncTime: 75,
  dataVolume: {
    totalEmails: 156847,
    totalLeads: 1247,
    totalReplies: 342,
    totalBounces: 89
  }
};

// Helper functions for demo data
export const getDemoSyncHistory = (days: number = 7) => {
  const cutoffDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
  return DEMO_SYNC_STATUS.syncHistory.filter(record => 
    new Date(record.date) >= cutoffDate
  );
};

export const getDemoHealthChecksByStatus = (status: string) => {
  return DEMO_SYNC_STATUS.healthChecks.filter(check => check.status === status);
};

export const getDemoRecentChangesByType = (type: string) => {
  return DEMO_SYNC_STATUS.recentChanges.filter(change => change.type === type);
};

export const getDemoSyncStats = () => {
  const history = DEMO_SYNC_STATUS.syncHistory;
  const totalSyncs = history.length;
  const successfulSyncs = history.filter(s => s.status === 'success').length;
  const warningSyncs = history.filter(s => s.status === 'warning').length;
  const errorSyncs = history.filter(s => s.status === 'error').length;
  const avgDuration = history.reduce((sum, s) => sum + s.duration, 0) / totalSyncs;
  const totalEmailsSynced = history.reduce((sum, s) => sum + s.emailsSynced, 0);
  const totalErrors = history.reduce((sum, s) => sum + s.errors, 0);

  return {
    totalSyncs,
    successfulSyncs,
    warningSyncs,
    errorSyncs,
    successRate: Math.round((successfulSyncs / totalSyncs) * 100 * 10) / 10,
    avgDuration: Math.round(avgDuration),
    totalEmailsSynced,
    totalErrors,
    errorRate: Math.round((totalErrors / totalEmailsSynced) * 100 * 100) / 100
  };
};
